// "5 minutes ago" wording for timestamps, the way Rails' time_ago_in_words
// reads. Stays relative forever ("2 years ago") — the exact moment rides on
// the <TimeAgo> hover title, never a cutover to a plain date.
//
// Built on the browser's Intl.RelativeTimeFormat: no library, and
// "yesterday" / "last month" come free from numeric: "auto".

const MINUTE = 60;
const HOUR = 3_600;
const DAY_MS = 86_400_000;

const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Monday of the date's week, at local midnight.
function weekStart(d: Date): number {
  const daysSinceMonday = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - daysSinceMonday).getTime();
}

// Under a day apart: elapsed minutes/hours, truncated (1h59m reads
// "1 hour ago"). A day or more: calendar counts, so the words match the
// calendar people read them against — "yesterday" is the calendar day
// before today, "next month" is the next calendar month (Jan 1 seen on
// Nov 4 reads "in 2 months", not "next month"), and weeks start Monday.
// The elapsed day count only picks the unit. `now` is injectable for tests.
export function timeAgo(iso: string, now: number = Date.now()): string {
  const thenDate = new Date(iso);
  const then = thenDate.getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.round((then - now) / 1000); // negative = past
  const abs = Math.abs(secs);
  if (abs < MINUTE) return "just now";
  if (abs < HOUR) return relative.format(Math.trunc(secs / MINUTE), "minute");
  if (abs < 24 * HOUR) return relative.format(Math.trunc(secs / HOUR), "hour");

  const nowDate = new Date(now);
  const days = Math.round((localMidnight(thenDate) - localMidnight(nowDate)) / DAY_MS);
  const absDays = Math.abs(days);
  if (absDays < 7) return relative.format(days, "day");
  if (absDays < 30) return relative.format(Math.round((weekStart(thenDate) - weekStart(nowDate)) / (7 * DAY_MS)), "week");
  if (absDays < 365) {
    const months = (thenDate.getFullYear() - nowDate.getFullYear()) * 12 + (thenDate.getMonth() - nowDate.getMonth());
    return relative.format(months, "month");
  }
  return relative.format(thenDate.getFullYear() - nowDate.getFullYear(), "year");
}

// The exact moment for a hover title: "Sep 3, 2026, 2:15 PM".
export function exactTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}
